import React, { useState } from 'react';
import Icon from '../AppIcon';

/**
 * Material Design 3 Profile Picture Component
 * Supports multiple sizes, fallback states with initials, and teal design tokens
 * Healthcare-appropriate styling with proper accessibility
 */
const ProfilePicture = ({
  src,
  alt = "Profile",
  displayName,
  size = "medium", // xs, sm, medium, lg, xl, 2xl
  variant = "circular", // circular, rounded
  fallbackIcon = "User",
  className = "",
  onClick,
  loading = false,
  imageLoading = "lazy",
  fetchPriority = "auto",
  online = false,
  verified = false,
  badge,
  ...props
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Material Design 3 size configurations
  const sizeConfig = {
    xs: {
      container: "w-6 h-6 text-xs",
      icon: 12,
      badge: "w-2 h-2 -top-0.5 -right-0.5",
      badgeText: "text-xs w-4 h-4 -top-1 -right-1",
      online: "w-1.5 h-1.5 border border-white -bottom-0.5 -right-0.5"
    },
    sm: {
      container: "w-8 h-8 text-sm",
      icon: 14,
      badge: "w-2.5 h-2.5 -top-0.5 -right-0.5",
      badgeText: "text-xs w-5 h-5 -top-1 -right-1",
      online: "w-2 h-2 border border-white -bottom-0.5 -right-0.5"
    },
    medium: {
      container: "w-10 h-10 text-base",
      icon: 16,
      badge: "w-3 h-3 -top-1 -right-1",
      badgeText: "text-xs w-6 h-6 -top-1.5 -right-1.5",
      online: "w-2.5 h-2.5 border-2 border-white -bottom-0.5 -right-0.5"
    },
    lg: {
      container: "w-12 h-12 text-lg",
      icon: 20,
      badge: "w-3.5 h-3.5 -top-1 -right-1",
      badgeText: "text-sm w-7 h-7 -top-1.5 -right-1.5",
      online: "w-3 h-3 border-2 border-white -bottom-1 -right-1"
    },
    xl: {
      container: "w-16 h-16 text-xl",
      icon: 24,
      badge: "w-4 h-4 -top-1.5 -right-1.5",
      badgeText: "text-sm w-8 h-8 -top-2 -right-2",
      online: "w-4 h-4 border-2 border-white -bottom-1 -right-1"
    },
    "2xl": {
      container: "w-20 h-20 text-2xl",
      icon: 32,
      badge: "w-5 h-5 -top-2 -right-2",
      badgeText: "text-base w-10 h-10 -top-2.5 -right-2.5",
      online: "w-5 h-5 border-2 border-white -bottom-1.5 -right-1.5"
    },
    account: {
      container: "w-[104px] h-[104px] text-2xl",
      icon: 32,
      badge: "w-5 h-5 -top-2 -right-2",
      badgeText: "text-base w-10 h-10 -top-2.5 -right-2.5",
      online: "w-5 h-5 border-2 border-white -bottom-1.5 -right-1.5"
    }
  };

  const currentSize = sizeConfig?.[size] || sizeConfig?.medium;

  // Generate initials from display name
  const getInitials = () => {
    if (!displayName) return "";
    const names = displayName?.trim()?.split(' ');
    if (names?.length === 1) {
      return names?.[0]?.charAt(0)?.toUpperCase();
    }
    return (names?.[0]?.charAt(0) + names?.[names?.length - 1]?.charAt(0))?.toUpperCase();
  };

  const initials = getInitials();

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <Icon 
            name="Loader2" 
            size={currentSize?.icon} 
            className="animate-spin text-primary-fixed-dim" 
          />
        </div>
      );
    }

    // Show image if available and not errored
    if (src && !imageError) {
      return (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-cover ${!imageLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
          onError={handleImageError}
          onLoad={handleImageLoad}
          loading={imageLoading}
          fetchPriority={fetchPriority}
          decoding="async"
        />
      );
    }

    // Show initials if available, otherwise show icon
    if (initials) {
      return (
        <div className="w-full h-full flex items-center justify-center font-medium text-primary-fixed select-none">
          {initials}
        </div>
      );
    }

    // Fallback to icon
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Icon 
          name={fallbackIcon} 
          size={currentSize?.icon} 
          className="text-primary-fixed"
        />
      </div>
    );
  };

  const containerClasses = [
    currentSize?.container,
    variant === "circular" ? "rounded-full" : "rounded-lg",
    "flex items-center justify-center flex-shrink-0 overflow-hidden",
    "bg-primary-container border-2 border-outline-variant",
    "medical-transition select-none",
    onClick ? "cursor-pointer hover:border-primary hover:shadow-sm" : "",
    verified ? "ring-2 ring-primary ring-offset-2 ring-offset-surface" : "",
    className
  ]?.filter(Boolean)?.join(" ");

  return (
    <div className="relative inline-block">
      <div
        className={containerClasses}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-label={onClick ? `${alt} profile picture` : alt}
        {...props}
      >
        {renderContent()}
      </div>

      {/* Online Status Indicator */}
      {online && (
        <div 
          className={`absolute ${currentSize?.online} bg-success rounded-full`}
          aria-label="Online"
        />
      )}

      {/* Verification Badge */}
      {verified && !badge && (
        <div 
          className={`absolute ${currentSize?.badge} bg-primary rounded-full flex items-center justify-center`}
          aria-label="Verified"
        >
          <Icon name="CheckCircle" size={8} className="text-primary-fixed" />
        </div>
      )}

      {/* Custom Badge */}
      {badge && typeof badge === 'number' && (
        <div 
          className={`absolute ${currentSize?.badgeText} bg-critical text-on-error rounded-full flex items-center justify-center font-medium min-w-0 text-center leading-none`}
          aria-label={`${badge} notifications`}
        >
          {badge > 99 ? '99+' : badge}
        </div>
      )}

      {badge && typeof badge === 'string' && (
        <div 
          className={`absolute ${currentSize?.badge} bg-tertiary rounded-full flex items-center justify-center`}
          aria-label={badge}
        >
          <Icon name={badge} size={8} className="text-on-tertiary" />
        </div>
      )}
    </div>
  );
};

export default ProfilePicture;
